import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../context/SiteSettingsContext';
import 'leaflet/dist/leaflet.css';
import './LocationPicker.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Used only until SiteSettingsContext finishes its first fetch — the real
// center comes from the operator-configured store_map_lat/store_map_lng.
const FALLBACK_MAP_CENTER = [40.7128, -74.0060];

function MapFlyTo({ target }) {
  const map = useMap();
  const prevRef = useRef(null);
  useEffect(() => {
    if (!target) return;
    const prev = prevRef.current;
    if (!prev || prev.lat !== target.lat || prev.lng !== target.lng) {
      map.flyTo([target.lat, target.lng], 15, { duration: 1 });
      prevRef.current = target;
    }
  }, [target, map]);
  return null;
}

function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
    }
  });
  return null;
}

function DraggableMarker({ position, onPositionChange }) {
  const markerRef = useRef(null);

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const pos = marker.getLatLng();
        onPositionChange({ lat: pos.lat, lng: pos.lng });
      }
    }
  };

  return (
    <Marker
      draggable
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    />
  );
}

const LocationPicker = ({ onChange, initialLat, initialLng, externalPosition }) => {
  const { t } = useTranslation();
  const { storeMapLat, storeMapLng } = useSiteSettings();
  const defaultMapCenter = (storeMapLat && storeMapLng)
    ? [storeMapLat, storeMapLng]
    : FALLBACK_MAP_CENTER;
  const [position, setPosition] = useState(
    initialLat && initialLng
      ? { lat: parseFloat(initialLat), lng: parseFloat(initialLng) }
      : null
  );

  useEffect(() => {
    if (externalPosition && externalPosition.lat && externalPosition.lng) {
      setPosition(externalPosition);
    }
  }, [externalPosition]);

  const [geoLoading, setGeoLoading] = useState(false);
  const [reverseResult, setReverseResult] = useState(null);

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      if (data && data.address) {
        setReverseResult({
          street: [data.address.road, data.address.house_number].filter(Boolean).join(' ') || '',
          city: data.address.city || data.address.town || data.address.village || '',
          postalCode: data.address.postcode || '',
        });
      }
    } catch {
      // Nominatim may fail; silently ignore
    }
  }, []);

  const handleLocationSelect = useCallback((pos) => {
    setPosition(pos);
    onChange(pos);
    reverseGeocode(pos.lat, pos.lng);
  }, [onChange, reverseGeocode]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (geo) => {
        const pos = { lat: geo.coords.latitude, lng: geo.coords.longitude };
        handleLocationSelect(pos);
        setGeoLoading(false);
      },
      () => setGeoLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (reverseResult && onChange) {
      onChange(position, reverseResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reverseResult]);

  return (
    <div className="location-picker">
      <div className="location-picker-header">
        <span className="location-label">{t('checkout.pinLocation', 'Pin your delivery location')}</span>
        <button
          type="button"
          className="use-location-btn"
          onClick={handleUseMyLocation}
          disabled={geoLoading}
        >
          {geoLoading
            ? t('checkout.locating', 'Locating...')
            : t('checkout.useMyLocation', 'Use my location')}
        </button>
      </div>

      <div className="location-map-container">
        <MapContainer
          center={position ? [position.lat, position.lng] : defaultMapCenter}
          zoom={position ? 15 : 12}
          style={{ height: '280px', width: '100%', borderRadius: '8px' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onLocationSelect={handleLocationSelect} />
          <MapFlyTo target={externalPosition} />
          {position && (
            <DraggableMarker
              position={[position.lat, position.lng]}
              onPositionChange={handleLocationSelect}
            />
          )}
        </MapContainer>
      </div>

      {!position && (
        <p className="location-hint">{t('checkout.clickMapHint', 'Click on the map to pin your location')}</p>
      )}

      {position && (
        <p className="location-coords">
          {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
        </p>
      )}
    </div>
  );
};

export default LocationPicker;
